import { formattedValues } from '@/model/contact-info.mjs';
import { DealerProfileType, TransferCountType } from '@masterffl/dealer-profile-component';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface DealerState {
  dealerDetails: DealerProfileType;
  transferCount: TransferCountType;
  loading: boolean;
  isEZCheckForTransferCreationInProgress: boolean;
  isTransferCreationAllowed: boolean;
  isTransferReinitiateAllowed: boolean;
  ezCheckStatusTransferCreation: string | null;
  ezCheckTransferCreationError: string | null;
  totalTransfersCount: number;
  sequalizeValidationError: string | null;
  isEzCheckHistoryInprogress: boolean;
  isPreferredLicense: {license:string, isLoading: boolean, isChecked: boolean};
  isLicenseRemoved: boolean;
  isEmailNotFoundLoading: boolean;
  callOcrStepFunctionApi: boolean;
  isValidationMaxAPIStop: boolean;
  ocrStepFunctionStatusV2: any;
  isOCRLoading: boolean;
  isManualReviewRequested: boolean;
  isManualReviewLoading: boolean;
  isEditable: boolean;
  dealerStoreDetails: unknown | null;
  dealerStoreDetailsLoading: boolean;
  dealerStoreDetailsError: boolean;
}

export interface ezCheckInitiateTransfer {
  license_key: string | undefined;
  isReinitiate?: boolean;
}

export const defaultDetailsDetails =  {
  _id: '',
  services: [],
  loa:[{
    loaIssueDate: '',
    loaExpiryDate: '',
    licenseKey: '',
    loaCertificateSignedUrl: '',
    isPending: false
  }],
  sot: {
    signedUrl: "",
    isSOTDealer: false,
    licenseKey: '',
    silencerShopId: '',
    dateOfTaxStampReceipt: '',
    annualTaxRate: null,
    numberOfLocations: null,
    specialTaxPeriodStartDate: '',
    specialTaxPeriodEndDate: '',
    controlNumber: null,
    employeeIdentificationNumber: null,
    expiryDate: '',
    licensePath: null,
    bioMetricScanner: false,
    typeOfOperation: null,
    principalBusinessName: "",
    principalBusinessAddress: "",
    actualPhysicalBusinessName: "",
    actualPhysicalBusinessAddress	: "",
    isPending:false,
  },
  businessDetails: {
    isTemporarilyClosed: false,
    blacklistedStores: [],
    primaryBusinessType: '',
    isCurrentlyOpen: false,
    isOptInForAll: false,
    isOptInForStore: false,
    isOptOutForAll: false,
    isOptOutForStore: false,
    isOptedOutForTransferFromNonFFLSeller: false,
    isPreferred: false,
    isRange: false,
    isStocking: false,
    hasValidCertificate: false,
    certificateRowId: 0,
    photos: [],
    phones: [],
    emails: [],
    websites: [],
    businessTypes: [],
    socialProfiles: [],
    isPermanentlyClosed: false,
    isBlackListed: false,
    stockingDealersForStore: [],
    bio: null,
    optChoices: {
      isOptOut: false,
      isOptIn: false,
      optedInStores: [],
      optedOutStores: []
    },
    operatingHours: {
      periods: [
        {
          open: {
            day: 1,
            time: ''
          },
          close: {
            day: 1,
            time: ''
          }
        }
      ],
      weekday_text: []
    },
    isDeleted: false,
    orderScore: 0,
    address: [],
    industryRelevance: false,
    contactNames: []
  },
  contactDetails: [],
  fees: {
    handGun: {
      value: '',
      enabled: false
    },
    longGun: {
      value: '',
      enabled: false
    },
    other: {
      value: '',
      enabled: false
    },
    nics: {
      value: '',
      enabled: false
    },
    nfaFee: {
      value: '',
      enabled: false
    },
    discountForMultipleFirearms: {
      value: '',
      enabled: false
    }
  },
  licenses: [
    {
      signedUrl: "",
      licenseKey: '',
      licenseName: '',
      phone: '',
      fullLicenseKey: '',
      isPrimaryForOutbound: false,
      isPrimaryForInbound: false,
      isTemporarilyClosed: false,
      type: '',
      expiryDate: '',
      hasValidCertificate: false,
      licenseCertificateExpiry: '',
      licenseDate: '',
      licensePath: '',
      printedName: '',
      printedPosition: '',
      isDeleted: false,
      isPermanentlyClosed: false,
      slug: '',
      businessName: '',
      premiseAddress: {
        state: '',
        zipCode: '',
        street: '',
        city: ''
      },
      mailingAddress: {
        state: '',
        zipCode: '',
        street: '',
        city: ''
      },
      loaStartDate: null,
      loaEndDate: null,
      cfd: 0,
    }
  ],
  primaryInboundDetails: {
    licenseKey: '',
    type: '',
    expiryDate: '',
    deliveryIndicator: '',
    industryRelevance: '',
    isShootingRange: false,
    isBigBoxRetailer: false,
    geoLocation: {
      lat: '',
      lng: ''
    },
    premiseAddress: {
      state: '',
      zipCode: '',
      street: '',
      city: ''
    },
    mailingAddress: {
      state: '',
      zipCode: '',
      street: '',
      city: ''
    },
    orderScore: 0,
    slug: '',
    businessName: '',
    isDeleted: false
  },
  ratingsAndReviews: {
    google: {
      ratings: '',
      reviews: 0
    },
    facebook: {
      ratings: '',
      reviews: 0
    },
    yelp: {
      ratings: '',
      reviews: 0
    },
    consolidatedRatingReviews: {
      ratings: '',
      reviews: 0
    }
  }
}

export const certificateWorkflowInitialState = {
  data: {
    id: 0,
    client_document_id: "",
    transfer_id: "",
    license_image_optimizer: null,
    ocr_check: null,
    license_validator: null,
    ez_validator: null,
    created_at: "",
    updated_at: "",
  },
  status: null,
};

const initialState: DealerState = {
  dealerDetails: defaultDetailsDetails,
  transferCount: {
    completedTransferCount: 0
  },
  loading: false,
  isEZCheckForTransferCreationInProgress: false,
  isTransferCreationAllowed: false,
  isTransferReinitiateAllowed: false,
  ezCheckStatusTransferCreation: null,
  ezCheckTransferCreationError: null,
  totalTransfersCount: 0,
  sequalizeValidationError: null,
  isEzCheckHistoryInprogress: false,
  isPreferredLicense: {license:'', isLoading: false, isChecked: false},
  isLicenseRemoved: false,
  isEmailNotFoundLoading: false,
  callOcrStepFunctionApi: false,
  isValidationMaxAPIStop: false,
  ocrStepFunctionStatusV2: {
    status: null
},
isOCRLoading: false,
isManualReviewRequested: false,
isManualReviewLoading: false,
isEditable: true,
dealerStoreDetails: null,
dealerStoreDetailsLoading: false,
dealerStoreDetailsError: false,
};

export const DealerSlice = createSlice({
  name: 'dealer',
  initialState,
  reducers: {
    setDealerDetails: (state, action: PayloadAction<DealerProfileType>) => {
      state.dealerDetails = action.payload;
      state.loading = false;
    },
    setTransferCount: (state, action: PayloadAction<TransferCountType>) => {
      state.transferCount = action.payload;
      state.loading = false;
    },
    toggleLoading: (state) => {
      state.loading = true;
    },
    setIsEzCheckHistoryInprogress: (state, action: PayloadAction<boolean>) => {
      state.isEzCheckHistoryInprogress = action.payload;
    },
    setIsPreferredLicense:(state,action: PayloadAction<{license:string, isLoading: boolean,  isChecked: boolean}>) => {
      state.isPreferredLicense = action.payload;
    },
    setIsEZCheckForTransferCreationInProgress: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isEZCheckForTransferCreationInProgress = action.payload;
    },
    setisTransferCreationAllowed: (state, action: PayloadAction<boolean>) => {
      state.isTransferCreationAllowed = action.payload;
    },
    setIsTransferReinitiateAllowed: (state, action: PayloadAction<boolean>) => {
      state.isTransferReinitiateAllowed = action.payload;
    },
    setEZCheckStatusTransferCreation: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.ezCheckStatusTransferCreation = action.payload;
    },
    setEZCheckForTransferCreationMessage: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.ezCheckTransferCreationError = action.payload;
    },
    setTotalTransfersCount: (state, action: PayloadAction<number>) => {
      state.totalTransfersCount = action.payload;
    },
    setSequalizeValidationError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.sequalizeValidationError = action.payload;
    },
    callToEZCheckForTransferCreation: (
      state,
      action: PayloadAction<ezCheckInitiateTransfer>
    ) => {},
    //saga
    callToGetDealerDetail: (state, action: PayloadAction<string>) => {},
    callToTransferCount: (state, action: PayloadAction<string>) => {},
    callToGetDealerSignedPhotos: (state, action: PayloadAction<string>) => {},
    callToToggleLoading: () => {},
    setIsNewProfileLicenseRemoved: (state, action: PayloadAction<boolean>) => {
      state.isLicenseRemoved = action.payload;
    },
    callToEmailNotFound: (state, action: PayloadAction<string>) => {},
    setIsEmailNotFoundLoading: (state, action: PayloadAction<boolean>) => {
      state.isEmailNotFoundLoading = action.payload;
    },
    setCallOcrStepFunctionApi: (state, action) => {
      state.callOcrStepFunctionApi = action.payload;
    },
    setIsValidationMaxAPIStop: (state, action: PayloadAction<boolean>) => {
      state.isValidationMaxAPIStop = action.payload
    },
    setOcrStepFunctionStatusV2: (state, action) => {
      state.ocrStepFunctionStatusV2 = action.payload;
    },
    setIsOCRLoading: (state, action: PayloadAction<boolean>) => {
      state.isOCRLoading = action.payload
    },
    callToGetOCRStepFunctionDetailsV2: (state, action: PayloadAction<{ clientDocumentId: string }>) => { },
    setIsManualReviewRequested: (state, action: PayloadAction<boolean>) => {
      state.isManualReviewRequested = action.payload
    },
    setIsManualReviewLoading: (state, action: PayloadAction<boolean>) => {
      state.isManualReviewLoading = action.payload
    },
    callToManualReviewRequest: (state, action: PayloadAction<{ clientDocumentId: string }>) => { },
    setIsEditable: (state, action: PayloadAction<boolean>) => {
      state.isEditable = action.payload
    },
    callToGetDealerStoreDetails: (state, _action: PayloadAction<string>) => {
      state.dealerStoreDetailsLoading = true;
      state.dealerStoreDetailsError = false;
      state.dealerStoreDetails = null;
    },
    setDealerStoreDetailsSuccess: (state, action: PayloadAction<unknown>) => {
      state.dealerStoreDetails = action.payload;
      state.dealerStoreDetailsLoading = false;
      state.dealerStoreDetailsError = false;
    },
    setDealerStoreDetailsFailure: (state) => {
      state.dealerStoreDetails = null;
      state.dealerStoreDetailsLoading = false;
      state.dealerStoreDetailsError = true;
    },
    clearDealerStoreDetails: (state) => {
      state.dealerStoreDetails = null;
      state.dealerStoreDetailsLoading = false;
      state.dealerStoreDetailsError = false;
    },
  }
});

export const {
  callToGetDealerDetail,
  callToTransferCount,
  setDealerDetails,
  setTransferCount,
  callToGetDealerSignedPhotos,
  setIsPreferredLicense,
  setIsEZCheckForTransferCreationInProgress,
  setIsTransferReinitiateAllowed,
  setisTransferCreationAllowed,
  setEZCheckStatusTransferCreation,
  setEZCheckForTransferCreationMessage,
  callToToggleLoading,
  toggleLoading,
  setTotalTransfersCount,
  setSequalizeValidationError,
  callToEZCheckForTransferCreation,
  setIsEzCheckHistoryInprogress,
  setIsNewProfileLicenseRemoved,
  callToEmailNotFound,
  setIsEmailNotFoundLoading,
  setCallOcrStepFunctionApi,
  setIsValidationMaxAPIStop,
  setOcrStepFunctionStatusV2,
  setIsOCRLoading,
  callToGetOCRStepFunctionDetailsV2,
  setIsManualReviewRequested,
  setIsManualReviewLoading,
  callToManualReviewRequest,
  setIsEditable,
  callToGetDealerStoreDetails,
  setDealerStoreDetailsSuccess,
  setDealerStoreDetailsFailure,
  clearDealerStoreDetails
} = DealerSlice.actions;

export default DealerSlice.reducer;
